import React from 'react';
import { Globe, TrendingUp, Shield, CheckCircle } from 'lucide-react';

function ForeignDTCBrands() {
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="text-5xl font-bold mb-6 text-[#000000]">
          Foreign DTC Brands Launching in Americas
        </h1>
        
        <p className="text-xl text-gray-700 mb-12">
          Breaking into the Americas market as a foreign DTC brand comes with unique challenges—from understanding local consumer behavior to navigating regulatory requirements and building supply chain infrastructure.
        </p>

        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6 text-[#000000]">The Challenges You Face</h2>
          <div className="space-y-4">
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <h3 className="text-xl font-semibold mb-2 text-[#000000]">Market Entry Complexity</h3>
              <p className="text-gray-700">Understanding US, Canadian, and LATAM consumer preferences, regulatory landscapes, and competitive dynamics requires local expertise.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <h3 className="text-xl font-semibold mb-2 text-[#000000]">Supply Chain & Tariffs</h3>
              <p className="text-gray-700">High tariffs, long shipping times, and complex logistics can erode margins and slow your market entry.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <h3 className="text-xl font-semibold mb-2 text-[#000000]">Brand Localization</h3>
              <p className="text-gray-700">Your brand messaging and positioning need to resonate with local audiences while maintaining authenticity.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <h3 className="text-xl font-semibold mb-2 text-[#000000]">Distribution Channels</h3>
              <p className="text-gray-700">Building relationships with retailers, understanding e-commerce platforms, and establishing fulfillment infrastructure.</p>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6 text-[#000000]">How Motif Unbound Helps</h2>
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#38A3A5] rounded-lg flex items-center justify-center flex-shrink-0">
                <Globe size={24} className="text-white" />
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2 text-[#000000]">Strategic Market Entry</h3>
                <p className="text-gray-700 mb-3">We provide end-to-end market entry services including market research, regulatory compliance, and go-to-market strategy development.</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-[#BB3E00] mt-1 flex-shrink-0" />
                    <span className="text-sm">Comprehensive market analysis and opportunity assessment</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-[#BB3E00] mt-1 flex-shrink-0" />
                    <span className="text-sm">Regulatory and compliance guidance</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-[#BB3E00] mt-1 flex-shrink-0" />
                    <span className="text-sm">Local partner and distributor identification</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#38A3A5] rounded-lg flex items-center justify-center flex-shrink-0">
                <Shield size={24} className="text-white" />
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2 text-[#000000]">Tariff Engineering & Supply Chain</h3>
                <p className="text-gray-700 mb-3">Reduce or eliminate tariffs through nearshoring strategies and optimize your supply chain for the Americas market.</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-[#BB3E00] mt-1 flex-shrink-0" />
                    <span className="text-sm">Mexico/Canada manufacturing setup for USMCA benefits</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-[#BB3E00] mt-1 flex-shrink-0" />
                    <span className="text-sm">Tariff optimization and duty drawback strategies</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-[#BB3E00] mt-1 flex-shrink-0" />
                    <span className="text-sm">US-based inventory management and fulfillment</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#38A3A5] rounded-lg flex items-center justify-center flex-shrink-0">
                <TrendingUp size={24} className="text-white" />
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2 text-[#000000]">Sales & Marketing Acceleration</h3>
                <p className="text-gray-700 mb-3">Launch and scale with proven strategies for online, specialty retail, and big box distribution.</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-[#BB3E00] mt-1 flex-shrink-0" />
                    <span className="text-sm">E-commerce platform optimization (Amazon, Shopify, etc.)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-[#BB3E00] mt-1 flex-shrink-0" />
                    <span className="text-sm">Retail channel development and partnerships</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-[#BB3E00] mt-1 flex-shrink-0" />
                    <span className="text-sm">Localized marketing campaigns and influencer partnerships</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#38A3A5] text-white p-8 rounded-2xl">
          <h2 className="text-2xl font-bold mb-4">Ready to Launch in the Americas?</h2>
          <p className="mb-6">Let's discuss your market entry strategy and how we can help you succeed.</p>
          <a
            href="mailto:contact@motifunbound.com?subject=Schedule%20a%20Consult&body=Hi%20Motif%20Unbound%20team,%0D%0A%0D%0AI%27d%20like%20to%20schedule%20a%20consultation%20to%20discuss%20our%20market%20expansion.%20Please%20share%20available%20times.%0D%0A%0D%0AThank%20you."
            className="inline-block bg-[#FFD965] hover:bg-[#f5cc4d] text-[#000000] px-8 py-3 rounded-lg font-semibold transition-all"
          >
            Schedule a Consult
          </a>
        </section>
      </div>
    </div>
  );
}

export default ForeignDTCBrands;
